import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import toastr from "toastr";
import SignaturePad from "../../../components/Common/SignaturePad";
import { useSubmitWorkerBookingAgreement } from "../../../api/booking";
import { buildSignedContractPdf, fetchPdfAsArrayBuffer } from "../../../utils/signedContractPdf";
import { mediaUrl } from "../../../helpers/mediaHelper";

const DEFAULT_SIGNATURE_AXIS = { x: 56, y: 56 };

function normalizePdfAxis(pdfAxis) {
  const x = Number(pdfAxis?.x);
  const y = Number(pdfAxis?.y);
  if (Number.isFinite(x) && Number.isFinite(y) && x >= 0 && y >= 0) {
    return { x, y, isFallback: false };
  }
  return { ...DEFAULT_SIGNATURE_AXIS, isFallback: true };
}

function buildAgreementFormData(agreementDocument) {
  const formData = new FormData();
  formData.append("agreementDocument", agreementDocument);
  return formData;
}

export default function BookingAgreementSignModal({
  isOpen,
  toggle,
  bookingRequestSlug,
  agreementPdf,
  pdfAxis,
  onSuccess,
}) {
  const { t } = useTranslation();
  const signaturePadRef = useRef(null);
  const [signatureDrawn, setSignatureDrawn] = useState(false);
  const [signatureError, setSignatureError] = useState(false);
  const [templateBuffer, setTemplateBuffer] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const submitMutation = useSubmitWorkerBookingAgreement();

  const axis = useMemo(() => normalizePdfAxis(pdfAxis), [pdfAxis]);
  const agreementPdfUrl = useMemo(() => mediaUrl(agreementPdf), [agreementPdf]);

  const loadTemplate = useCallback(async () => {
    if (!agreementPdfUrl) {
      setTemplateBuffer(null);
      return;
    }
    setTemplateLoading(true);
    try {
      const buffer = await fetchPdfAsArrayBuffer(agreementPdfUrl);
      setTemplateBuffer(buffer);
    } catch {
      setTemplateBuffer(null);
      toastr.error(
        t("workerAgreementSignLoadPdfFailed", "Failed to load agreement PDF.")
      );
    } finally {
      setTemplateLoading(false);
    }
  }, [agreementPdfUrl, t]);

  useEffect(() => {
    if (!isOpen) return;
    setSignatureDrawn(false);
    setSignatureError(false);
    loadTemplate();
  }, [isOpen, loadTemplate]);

  const handleSignatureChange = useCallback((hasSignature) => {
    setSignatureDrawn(hasSignature);
    if (hasSignature) setSignatureError(false);
  }, []);

  const handleSubmit = async () => {
    if (!bookingRequestSlug) return;
    if (!signatureDrawn) {
      setSignatureError(true);
      return;
    }
    if (!templateBuffer) {
      toastr.error(
        t(
          "workerAgreementSignPdfNotReady",
          "Agreement PDF is not ready yet. Please try again."
        )
      );
      return;
    }

    const canvas = signaturePadRef.current?.getCanvas();
    let signedFile = null;

    try {
      if (axis.isFallback) {
        toastr.warning(
          t(
            "workerAgreementSignAxisFallback",
            "Signature axis was missing. Used default placement."
          )
        );
      }
      signedFile = await buildSignedContractPdf(canvas, templateBuffer, {
        fileName: `booking-agreement-${bookingRequestSlug}.pdf`,
        marginLeftPt: axis.x,
        marginBottomPt: axis.y,
      });
    } catch {
      signedFile = null;
    }

    if (!signedFile) {
      toastr.error(
        t(
          "workerAgreementSignPrepareFailed",
          "Could not prepare signed agreement. Please try again."
        )
      );
      return;
    }

    const formData = buildAgreementFormData(signedFile);
    submitMutation.mutate(
      { bookingRequestSlug, formData },
      {
        onSuccess: () => {
          toastr.success(
            t(
              "workerAgreementSignSuccess",
              "Agreement submitted successfully."
            )
          );
          toggle();
          onSuccess?.();
        },
      }
    );
  };

  const busy = submitMutation.isPending || templateLoading;

  return (
    <Modal isOpen={isOpen} toggle={busy ? undefined : toggle} size="xl">
      <ModalHeader toggle={busy ? undefined : toggle}>
        {t("workerAgreementSignTitle", "Sign Agreement")}
      </ModalHeader>
      <ModalBody>
        {!agreementPdf ? (
          <Alert color="warning" className="mb-3">
            {t(
              "workerAgreementSignPdfMissing",
              "Agreement PDF is missing for this booking request."
            )}
          </Alert>
        ) : (
          <div className="mb-3">
            <iframe
              title={`agreement-preview-${bookingRequestSlug}`}
              src={agreementPdfUrl}
              className="w-100 rounded border"
              style={{ minHeight: "460px" }}
            />
          </div>
        )}

        <SignaturePad
          ref={signaturePadRef}
          label={t("workerAgreementSignSignatureLabel", "Signature")}
          hint={t(
            "workerAgreementSignSignatureHint",
            "Draw your signature to sign this agreement."
          )}
          onSignatureChange={handleSignatureChange}
          showError={signatureError}
          errorMessage={t(
            "workerAgreementSignSignatureRequired",
            "Please provide your signature before submitting."
          )}
        />
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={toggle} disabled={busy}>
          {t("cancel", "Cancel")}
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit}
          disabled={busy || !agreementPdf || !bookingRequestSlug}
          className="d-inline-flex align-items-center"
        >
          {submitMutation.isPending ? (
            <>
              <Spinner size="sm" className="me-2" />
              {t("submitting", "Submitting...")}
            </>
          ) : templateLoading ? (
            <>
              <Spinner size="sm" className="me-2" />
              {t("workerAgreementSignPreparing", "Preparing...")}
            </>
          ) : (
            t("workerAgreementSignSubmit", "Submit Agreement")
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

BookingAgreementSignModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  bookingRequestSlug: PropTypes.string,
  agreementPdf: PropTypes.string,
  pdfAxis: PropTypes.shape({
    x: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    y: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  onSuccess: PropTypes.func,
};
